import { useMemo, useRef,useState } from 'react'
import { Table } from 'react-bootstrap'
import SpinnerLoading from '../spinner-loading'
import { Checkbox } from 'antd'

const TableRowItem = ({
  item,
  index,
  baseCell,
  checkbox,
  showRowNumber,
  rowStartIndex,
  rowKey,
  fnUpdateSelect,
  renderRow,
  activeTab
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const currentBaseCell = {
    ...baseCell,
    verticalAlign: isExpanded ? 'top' : 'middle'
  }

  return (
    <tr>
      {checkbox && (
        <td
          style={{
            textAlign: 'center',
            verticalAlign: isExpanded ? 'top' : 'middle',
            padding: '16px 12px'
          }}
        >
          <Checkbox
            className="generic-table-wrapper"
            checked={item.is_check}
            onChange={(e) => fnUpdateSelect(item[rowKey], e.target.checked)}
          />
        </td>
      )}

      {showRowNumber && (
        <td
          style={{
            ...currentBaseCell,
            textAlign: 'center',
            padding: '16px 12px',
            color: 'var(--color-on-base-content-high)',
            fontSize: '14px',
            fontWeight: 400
          }}
        >
          {rowStartIndex + index + 1}
        </td>
      )}

      {renderRow(item, index, currentBaseCell, activeTab, setIsExpanded)}
    </tr>
  )
}

const GenericTable = ({
  columns = [],
  data = [],
  setData,
  isLoading = false,
  renderRow,
  keyExtractor,
  rowStartIndex = 0,
  showRowNumber = true,
  emptyMessage = '-No Data-',
  rowHeight = 60,
  visible_rows = 10,
  checkbox,
  activeTab,
  rowKey = 'id',
  setIsCheckDisable,
  onCheckedChange,
  onSelectAllChange
}) => {
  const checkedMap = useRef({})
  const assignMap = useRef({})

  // sync data กับ map
  data.forEach((item) => {
    const id = item[rowKey]

    if (checkedMap.current[id] === undefined) {
      checkedMap.current[id] = item.is_check ?? item.assign ?? false
    }

    if (assignMap.current[id] === undefined) {
      assignMap.current[id] = item.assign ?? false
    }

    item.is_check = checkedMap.current[id]
  })

  const total = data.length
  const checkedCount = data.filter((d) => d.is_check).length

  const colSpan = columns.length + (showRowNumber ? 1 : 0)
  const HEADER_HEIGHT = rowHeight

  const DEFAULT_VISIBLE_ROWS = visible_rows || 10

  const maxHeight = useMemo(() => {
    return (
      HEADER_HEIGHT + rowHeight * DEFAULT_VISIBLE_ROWS + DEFAULT_VISIBLE_ROWS
    )
  }, [rowHeight, HEADER_HEIGHT, DEFAULT_VISIBLE_ROWS])

  const baseCell = {
    height: rowHeight,
    verticalAlign: 'middle',
    padding: '0 12px',
    background: '#fff',
    fontSize: '14px',
    fontWeight: 400
  }

  const updateDisableState = () => {
    const changed = Object.keys(checkedMap.current).some((key) => {
      return checkedMap.current[key] !== assignMap.current[key]
    })

    setIsCheckDisable?.(!changed)
  }

  const fnUpdateSelect = (id, is_check) => {
    checkedMap.current[id] = is_check
    onCheckedChange?.(id, is_check)
    const newList = data.map((item) =>
      item[rowKey] === id ? { ...item, is_check } : item
    )
    setData(newList)
    updateDisableState()
  }

  const onSelectAll = (is_check) => {
    const newList = data.map((item) => {
      checkedMap.current[item[rowKey]] = is_check
      return { ...item, is_check }
    })
    onSelectAllChange?.(data, is_check) 
    setData(newList)
    updateDisableState()
  }

  return (
    <div
      style={{
        overflowY: 'auto',
        maxHeight,
        background: '#fff'
      }}
    >
      <Table className="generic-table">
        <thead>
          <tr>
            {checkbox && (
              <th
                className="text-center"
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 20,
                  height: HEADER_HEIGHT,
                  background: '#ECECEC',
                  borderBottom: '1px solid #E4E7EE',
                  minWidth: 60,
                  textAlign: 'center',
                  padding: '0 12px',
                  verticalAlign: 'middle'
                }}
              >
                <Checkbox
                  className="generic-table-wrapper"
                  checked={checkedCount === total && total > 0}
                  indeterminate={checkedCount > 0 && checkedCount < total}
                  onChange={(e) => onSelectAll(e.target.checked)}
                />
              </th>
            )}

            {showRowNumber && (
              <th
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  height: HEADER_HEIGHT,
                  background: 'var(--color-on-base-foreground-default)',
                  borderBottom: '1px solid var(--color-on-base-border-disable)',
                  minWidth: 50,
                  textAlign: 'center',
                  padding: '0 6px',
                  verticalAlign: 'middle',
                  fontWeight: '500',
                  fontSize: '14px',
                  color: 'var(--color-on-base-content-high)'
                }}
              >
                #
              </th>
            )}

            {columns.map((c, i) => (
              <th
                key={i}
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  height: HEADER_HEIGHT,
                  color: 'var(--color-on-base-content-high)',
                  background: 'var(--color-on-base-foreground-default)',
                  borderBottom: '1px solid var(--color-on-base-border-disable)',
                  minWidth: c.minWidth,
                  padding: '0px 12px',
                  ...c.headerStyle
                }}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {isLoading ? (
            <tr>
              <td
                colSpan={colSpan}
                style={{ ...baseCell, textAlign: 'center' }}
              >
                <SpinnerLoading />
              </td>
            </tr>
          ) : data.length ? (
            data.map((item, index) => {
              const key = keyExtractor
                ? keyExtractor(item, index)
                : `${rowStartIndex}-${index}`

              return (
                <TableRowItem
                  key={key}
                  item={item}
                  index={index}
                  baseCell={baseCell}
                  checkbox={checkbox}
                  showRowNumber={showRowNumber}
                  rowStartIndex={rowStartIndex}
                  rowKey={rowKey}
                  fnUpdateSelect={fnUpdateSelect}
                  renderRow={renderRow}
                  activeTab={activeTab}
                />
              )
            })
          ) : (
            <tr>
              <td
                colSpan={colSpan}
                style={{ ...baseCell, textAlign: 'center' }}
              >
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}

export default GenericTable
